package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelCategory
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelProductDetail
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelProductUnitPriceChange
import br.com.vvrefeicoes.portal.feature.operacional.ChartCard
import br.com.vvrefeicoes.portal.feature.operacional.HoverTooltip
import br.com.vvrefeicoes.portal.feature.operacional.ValueBar
import br.com.vvrefeicoes.portal.feature.operacional.compactMoney
import br.com.vvrefeicoes.portal.feature.operacional.rememberValueAxis
import io.github.koalaplot.core.bar.HorizontalBarPlot
import io.github.koalaplot.core.xygraph.CategoryAxisModel
import io.github.koalaplot.core.xygraph.XYGraph
import io.github.koalaplot.core.xygraph.rememberAxisContent
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.compras_col_product
import portalvv.shared.generated.resources.compras_panel_axis_variation_value
import portalvv.shared.generated.resources.compras_panel_categories_increase
import portalvv.shared.generated.resources.compras_panel_categories_reduction
import portalvv.shared.generated.resources.compras_panel_col_category
import portalvv.shared.generated.resources.compras_panel_col_current_total
import portalvv.shared.generated.resources.compras_panel_col_increase
import portalvv.shared.generated.resources.compras_panel_col_previous_total
import portalvv.shared.generated.resources.compras_panel_col_reduction
import portalvv.shared.generated.resources.compras_panel_col_variation_percent
import portalvv.shared.generated.resources.compras_panel_col_variation_value
import portalvv.shared.generated.resources.compras_panel_detailed_results
import portalvv.shared.generated.resources.compras_panel_month_total
import portalvv.shared.generated.resources.compras_panel_no_category_increases
import portalvv.shared.generated.resources.compras_panel_no_category_reductions
import portalvv.shared.generated.resources.compras_panel_no_data
import portalvv.shared.generated.resources.compras_panel_no_increase
import portalvv.shared.generated.resources.compras_panel_no_reduction
import portalvv.shared.generated.resources.compras_panel_top_increases_table
import portalvv.shared.generated.resources.compras_panel_top_reductions_table
import portalvv.shared.generated.resources.compras_panel_total
import kotlin.math.abs
import kotlin.math.roundToLong

private val MONTHLY_CHANGES_SIDE_BY_SIDE = 1050.dp
private val MONTHLY_CHANGES_TABLE_MIN_WIDTH = 650.dp
private const val MONTHLY_WEIGHT_PRODUCT = 2f
private const val MONTHLY_WEIGHT_MONEY = 1.15f
private const val MONTHLY_WEIGHT_PERCENT = 0.9f
private const val MONTHLY_WEIGHT_CATEGORY_NAME = 2f
private const val MONTHLY_WEIGHT_CATEGORY_VALUE = 1.2f
private const val MONTHLY_WEIGHT_CATEGORY_PERCENT = 1f

@Composable
internal fun MonthlyCategoryOverviewTables(categories: List<PurchasesPanelCategory>) {
    val reductions =
        categories
            .mapNotNull { category ->
                val variation = category.variationPercent
                if (variation != null && variation < 0 && category.totalCents > 0L) category else null
            }.sortedBy { it.variationPercent ?: 0.0 }
    val increases =
        categories
            .mapNotNull { category ->
                val variation = category.variationPercent
                if (variation != null && variation > 0 && category.totalCents > 0L) category else null
            }.sortedByDescending { it.variationPercent ?: 0.0 }
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        if (maxWidth >= 900.dp) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
                verticalAlignment = Alignment.Top,
            ) {
                MonthlyCategoryOverviewTable(
                    title = stringResource(Res.string.compras_panel_categories_reduction),
                    categories = reductions,
                    reduction = true,
                    emptyMessage = stringResource(Res.string.compras_panel_no_category_reductions),
                    modifier = Modifier.weight(1f),
                )
                MonthlyCategoryOverviewTable(
                    title = stringResource(Res.string.compras_panel_categories_increase),
                    categories = increases,
                    reduction = false,
                    emptyMessage = stringResource(Res.string.compras_panel_no_category_increases),
                    modifier = Modifier.weight(1f),
                )
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
                MonthlyCategoryOverviewTable(
                    title = stringResource(Res.string.compras_panel_categories_reduction),
                    categories = reductions,
                    reduction = true,
                    emptyMessage = stringResource(Res.string.compras_panel_no_category_reductions),
                )
                MonthlyCategoryOverviewTable(
                    title = stringResource(Res.string.compras_panel_categories_increase),
                    categories = increases,
                    reduction = false,
                    emptyMessage = stringResource(Res.string.compras_panel_no_category_increases),
                )
            }
        }
    }
}

@Composable
private fun MonthlyCategoryOverviewTable(
    title: String,
    categories: List<PurchasesPanelCategory>,
    reduction: Boolean,
    emptyMessage: String,
    modifier: Modifier = Modifier,
) {
    val valueColor = if (reduction) AppPalette.Success900 else AppPalette.Error900
    val totalCents = categories.sumOf { it.totalCents - it.previousTotalCents }
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
        Text(
            text = title,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
        )
        if (categories.isEmpty()) {
            Text(
                text = emptyMessage,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            Surface(
                modifier = Modifier.fillMaxWidth(),
                shape = AppShapes.medium,
                color = MaterialTheme.colorScheme.surface,
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
            ) {
                Column(modifier = Modifier.fillMaxWidth()) {
                    MonthlyCategoryOverviewHeader(valueColor = valueColor, reduction = reduction)
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    categories.forEachIndexed { index, category ->
                        MonthlyCategoryOverviewRow(category = category, valueColor = valueColor)
                        if (index < categories.lastIndex) {
                            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                        }
                    }
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    Row(
                        modifier =
                            Modifier
                                .fillMaxWidth()
                                .background(valueColor.copy(alpha = 0.10f))
                                .padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space8),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = stringResource(Res.string.compras_panel_total),
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = FontWeight.SemiBold,
                            color = valueColor,
                            modifier = Modifier.weight(MONTHLY_WEIGHT_CATEGORY_NAME),
                        )
                        Text(
                            text = signedMoney(totalCents),
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = FontWeight.SemiBold,
                            color = valueColor,
                            textAlign = TextAlign.End,
                            modifier = Modifier.weight(MONTHLY_WEIGHT_CATEGORY_VALUE),
                        )
                        Spacer(modifier = Modifier.weight(MONTHLY_WEIGHT_CATEGORY_PERCENT))
                    }
                }
            }
        }
    }
}

@Composable
private fun MonthlyCategoryOverviewHeader(
    valueColor: Color,
    reduction: Boolean,
) {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .background(valueColor.copy(alpha = 0.10f))
                .padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        MonthlyCategoryOverviewHeaderCell(
            stringResource(Res.string.compras_panel_col_category),
            MONTHLY_WEIGHT_CATEGORY_NAME,
            TextAlign.Start,
            valueColor,
        )
        MonthlyCategoryOverviewHeaderCell(
            stringResource(if (reduction) Res.string.compras_panel_col_reduction else Res.string.compras_panel_col_increase),
            MONTHLY_WEIGHT_CATEGORY_VALUE,
            TextAlign.End,
            valueColor,
        )
        MonthlyCategoryOverviewHeaderCell(
            stringResource(Res.string.compras_panel_col_variation_percent),
            MONTHLY_WEIGHT_CATEGORY_PERCENT,
            TextAlign.End,
            valueColor,
        )
    }
}

@Composable
private fun RowScope.MonthlyCategoryOverviewHeaderCell(
    text: String,
    weight: Float,
    textAlign: TextAlign,
    color: Color,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelSmall,
        color = color,
        textAlign = textAlign,
        modifier = Modifier.weight(weight),
    )
}

@Composable
private fun MonthlyCategoryOverviewRow(
    category: PurchasesPanelCategory,
    valueColor: Color,
) {
    val changeCents = category.totalCents - category.previousTotalCents
    val categoryUi = PurchasesCategory.entries.firstOrNull { it.type == category.type }
    val name = categoryUi?.let { stringResource(it.label) } ?: category.type.name
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
            modifier = Modifier.weight(MONTHLY_WEIGHT_CATEGORY_NAME),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            categoryUi?.let {
                Icon(
                    imageVector = it.icon,
                    contentDescription = null,
                    tint = it.iconColor,
                    modifier = Modifier.size(18.dp),
                )
            }
            Text(
                text = name,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurface,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f),
            )
        }
        Text(
            text = signedMoney(changeCents),
            style = MaterialTheme.typography.bodySmall,
            color = valueColor,
            textAlign = TextAlign.End,
            modifier = Modifier.weight(MONTHLY_WEIGHT_CATEGORY_VALUE),
        )
        Text(
            text = variationText(category.variationPercent),
            style = MaterialTheme.typography.bodySmall,
            color = valueColor,
            textAlign = TextAlign.End,
            modifier = Modifier.weight(MONTHLY_WEIGHT_CATEGORY_PERCENT),
        )
    }
}

@Composable
internal fun MonthlyCategoryChangesSections(
    reductions: List<PurchasesPanelProductUnitPriceChange>,
    increases: List<PurchasesPanelProductUnitPriceChange>,
    previousMonth: MonthPeriod?,
    currentMonth: MonthPeriod?,
) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
        MonthlyCategoryChangeSection(
            changes = reductions,
            reduction = true,
            previousMonth = previousMonth,
            currentMonth = currentMonth,
        )
        MonthlyCategoryChangeSection(
            changes = increases,
            reduction = false,
            previousMonth = previousMonth,
            currentMonth = currentMonth,
        )
    }
}

@Composable
private fun MonthlyCategoryChangeSection(
    changes: List<PurchasesPanelProductUnitPriceChange>,
    reduction: Boolean,
    previousMonth: MonthPeriod?,
    currentMonth: MonthPeriod?,
) {
    val sectionTitle =
        stringResource(
            if (reduction) {
                Res.string.compras_panel_top_reductions_table
            } else {
                Res.string.compras_panel_top_increases_table
            },
        )
    val emptyMessage =
        stringResource(
            if (reduction) {
                Res.string.compras_panel_no_reduction
            } else {
                Res.string.compras_panel_no_increase
            },
        )
    val orderedChanges =
        if (reduction) {
            changes.sortedBy { it.totalChangeCents }
        } else {
            changes.sortedByDescending { it.totalChangeCents }
        }

    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
        Text(
            text = sectionTitle,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
        )
        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            if (maxWidth >= MONTHLY_CHANGES_SIDE_BY_SIDE) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
                    verticalAlignment = Alignment.Top,
                ) {
                    MonthlyCategoryChangeTable(
                        changes = orderedChanges,
                        reduction = reduction,
                        emptyMessage = emptyMessage,
                        previousMonth = previousMonth,
                        currentMonth = currentMonth,
                        modifier = Modifier.weight(1.25f),
                    )
                    MonthlyCategoryChangeChart(
                        changes = orderedChanges,
                        reduction = reduction,
                        emptyMessage = emptyMessage,
                        modifier = Modifier.weight(1f),
                    )
                }
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
                    MonthlyCategoryChangeTable(
                        changes = orderedChanges,
                        reduction = reduction,
                        emptyMessage = emptyMessage,
                        previousMonth = previousMonth,
                        currentMonth = currentMonth,
                    )
                    MonthlyCategoryChangeChart(
                        changes = orderedChanges,
                        reduction = reduction,
                        emptyMessage = emptyMessage,
                    )
                }
            }
        }
    }
}

@Composable
private fun MonthlyCategoryChangeTable(
    changes: List<PurchasesPanelProductUnitPriceChange>,
    reduction: Boolean,
    emptyMessage: String,
    previousMonth: MonthPeriod?,
    currentMonth: MonthPeriod?,
    modifier: Modifier = Modifier,
) {
    BoxWithConstraints(modifier = modifier) {
        val tableWidth =
            if (maxWidth < MONTHLY_CHANGES_TABLE_MIN_WIDTH) {
                MONTHLY_CHANGES_TABLE_MIN_WIDTH
            } else {
                maxWidth
            }
        val scrollState = rememberScrollState()
        Box(modifier = Modifier.fillMaxWidth().horizontalScroll(scrollState)) {
            Surface(
                modifier = Modifier.width(tableWidth),
                shape = AppShapes.medium,
                color = MaterialTheme.colorScheme.surface,
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
            ) {
                Column(modifier = Modifier.fillMaxWidth()) {
                    MonthlyCategoryChangeHeader(
                        previousMonth = previousMonth,
                        currentMonth = currentMonth,
                        valuesAreTotals = true,
                    )
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    if (changes.isEmpty()) {
                        Box(
                            modifier =
                                Modifier
                                    .fillMaxWidth()
                                    .padding(vertical = AppSpacing.space24),
                            contentAlignment = Alignment.Center,
                        ) {
                            Text(
                                text = emptyMessage,
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                        }
                    } else {
                        changes.forEachIndexed { index, change ->
                            MonthlyCategoryChangeRow(change = change, reduction = reduction)
                            if (index < changes.lastIndex) {
                                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun MonthlyCategoryChangeHeader(
    previousMonth: MonthPeriod?,
    currentMonth: MonthPeriod?,
    valuesAreTotals: Boolean,
) {
    val previousPeriodLabel =
        previousMonth?.let(MonthCalendar::label)
            ?: stringResource(Res.string.compras_panel_col_previous_total)
    val currentPeriodLabel =
        currentMonth?.let(MonthCalendar::label)
            ?: stringResource(Res.string.compras_panel_col_current_total)
    val previousLabel =
        if (valuesAreTotals) {
            stringResource(Res.string.compras_panel_month_total, previousPeriodLabel)
        } else {
            previousPeriodLabel
        }
    val currentLabel =
        if (valuesAreTotals) {
            stringResource(Res.string.compras_panel_month_total, currentPeriodLabel)
        } else {
            currentPeriodLabel
        }
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .background(AppPalette.Neutral50)
                .padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        MonthlyCategoryHeaderCell(
            text = stringResource(Res.string.compras_col_product),
            weight = MONTHLY_WEIGHT_PRODUCT,
            textAlign = TextAlign.Start,
        )
        MonthlyCategoryHeaderCell(
            text = previousLabel,
            weight = MONTHLY_WEIGHT_MONEY,
            textAlign = TextAlign.End,
        )
        MonthlyCategoryHeaderCell(
            text = currentLabel,
            weight = MONTHLY_WEIGHT_MONEY,
            textAlign = TextAlign.End,
        )
        MonthlyCategoryHeaderCell(
            text = stringResource(Res.string.compras_panel_col_variation_percent),
            weight = MONTHLY_WEIGHT_PERCENT,
            textAlign = TextAlign.End,
        )
        MonthlyCategoryHeaderCell(
            text = stringResource(Res.string.compras_panel_col_variation_value),
            weight = MONTHLY_WEIGHT_MONEY,
            textAlign = TextAlign.End,
        )
    }
}

@Composable
private fun RowScope.MonthlyCategoryHeaderCell(
    text: String,
    weight: Float,
    textAlign: TextAlign,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelSmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        textAlign = textAlign,
        modifier = Modifier.weight(weight),
    )
}

@Composable
private fun MonthlyCategoryChangeRow(
    change: PurchasesPanelProductUnitPriceChange,
    reduction: Boolean,
) {
    val changeColor = if (reduction) AppPalette.Success900 else AppPalette.Error900
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = change.productName,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurface,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(MONTHLY_WEIGHT_PRODUCT),
        )
        MonthlyCategoryValueCell(
            text = money(change.previousTotalCents),
            weight = MONTHLY_WEIGHT_MONEY,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        MonthlyCategoryValueCell(
            text = money(change.currentTotalCents),
            weight = MONTHLY_WEIGHT_MONEY,
            color = MaterialTheme.colorScheme.onSurface,
        )
        MonthlyCategoryValueCell(
            text = variationText(change.totalVariationPercent),
            weight = MONTHLY_WEIGHT_PERCENT,
            color = changeColor,
        )
        MonthlyCategoryValueCell(
            text = signedMoney(change.totalChangeCents),
            weight = MONTHLY_WEIGHT_MONEY,
            color = changeColor,
        )
    }
}

@Composable
private fun RowScope.MonthlyCategoryValueCell(
    text: String,
    weight: Float,
    color: Color,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodySmall,
        color = color,
        textAlign = TextAlign.End,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = Modifier.weight(weight),
    )
}

@Composable
private fun MonthlyCategoryChangeChart(
    changes: List<PurchasesPanelProductUnitPriceChange>,
    reduction: Boolean,
    emptyMessage: String,
    modifier: Modifier = Modifier,
) {
    ChartCard(modifier = modifier) {
        if (changes.isEmpty()) {
            Text(
                text = emptyMessage,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            val chartChanges = changes.sortedByDescending { abs(it.totalChangeCents) }
            val ordered = chartChanges.reversed()
            val names = ordered.map { it.productName }
            val values = ordered.map { abs(it.totalChangeCents) / 100f }
            val barColor = if (reduction) AppPalette.Success500 else AppPalette.Error500
            XYGraph(
                gridStyle = NoGridStyle,
                xAxisModel = rememberValueAxis(values.maxOrNull() ?: 0f, headroom = 1.2f),
                yAxisModel = CategoryAxisModel(names),
                xAxisContent =
                    rememberAxisContent(
                        title = {
                            Text(
                                stringResource(Res.string.compras_panel_axis_variation_value),
                                style = MaterialTheme.typography.labelSmall,
                            )
                        },
                        labels = { value ->
                            Text(
                                compactMoney((value * 100).roundToLong()),
                                style = MaterialTheme.typography.labelSmall,
                            )
                        },
                    ),
                yAxisContent = rememberAxisContent(labels = { name -> Text(name, style = MaterialTheme.typography.labelSmall) }),
                modifier = Modifier.fillMaxWidth().height((changes.size * 44 + 48).dp),
            ) {
                HorizontalBarPlot(
                    xData = values,
                    yData = names,
                    barWidth = 0.6f,
                    bar = { index, _, _ ->
                        val change = ordered.getOrNull(index)
                        val label =
                            if (change == null) {
                                ""
                            } else {
                                money(abs(change.totalChangeCents))
                            }
                        val tooltip =
                            if (change == null) {
                                ""
                            } else {
                                "${change.productName}\n" +
                                    "${money(change.previousTotalCents)} -> ${money(change.currentTotalCents)}\n" +
                                    label
                            }
                        HoverTooltip(text = tooltip) {
                            ValueBar(
                                color = barColor,
                                label = label,
                                horizontal = true,
                            )
                        }
                    },
                )
            }
        }
    }
}

@Composable
internal fun MonthlyProductDetailsSection(
    details: List<PurchasesPanelProductDetail>,
    previousMonth: MonthPeriod?,
    currentMonth: MonthPeriod?,
) {
    var query by remember(details) { mutableStateOf("") }
    val filtered = remember(details, query) { filterProductDetails(details, query) }
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
        Text(
            text = stringResource(Res.string.compras_panel_detailed_results),
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
        )
        ProductDetailsSearch(query = query, onQueryChange = { query = it })
        if (filtered.isEmpty()) {
            Text(
                text = stringResource(Res.string.compras_panel_no_data),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            MonthlyProductDetailsTable(
                details = filtered,
                previousMonth = previousMonth,
                currentMonth = currentMonth,
            )
        }
    }
}

@Composable
private fun MonthlyProductDetailsTable(
    details: List<PurchasesPanelProductDetail>,
    previousMonth: MonthPeriod?,
    currentMonth: MonthPeriod?,
) {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val tableWidth =
            if (maxWidth < MONTHLY_CHANGES_TABLE_MIN_WIDTH) {
                MONTHLY_CHANGES_TABLE_MIN_WIDTH
            } else {
                maxWidth
            }
        val scrollState = rememberScrollState()
        Box(modifier = Modifier.fillMaxWidth().horizontalScroll(scrollState)) {
            Surface(
                modifier = Modifier.width(tableWidth),
                shape = AppShapes.medium,
                color = MaterialTheme.colorScheme.surface,
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
            ) {
                Column(modifier = Modifier.fillMaxWidth()) {
                    MonthlyCategoryChangeHeader(
                        previousMonth = previousMonth,
                        currentMonth = currentMonth,
                        valuesAreTotals = false,
                    )
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    details.forEachIndexed { index, detail ->
                        MonthlyProductDetailRow(detail = detail)
                        if (index < details.lastIndex) {
                            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun MonthlyProductDetailRow(detail: PurchasesPanelProductDetail) {
    val variationColor =
        when {
            detail.variationPercent == null -> MaterialTheme.colorScheme.onSurfaceVariant
            detail.variationPercent < 0.0 -> AppPalette.Success900
            detail.variationPercent > 0.0 -> AppPalette.Error900
            else -> MaterialTheme.colorScheme.onSurface
        }
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space12, vertical = AppSpacing.space8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = detail.productName,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurface,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(MONTHLY_WEIGHT_PRODUCT),
        )
        MonthlyCategoryValueCell(
            text = detail.previousUnitPriceCents?.let { money(it) } ?: NO_TABLE_VALUE,
            weight = MONTHLY_WEIGHT_MONEY,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        MonthlyCategoryValueCell(
            text = detail.currentUnitPriceCents?.let { money(it) } ?: NO_TABLE_VALUE,
            weight = MONTHLY_WEIGHT_MONEY,
            color = MaterialTheme.colorScheme.onSurface,
        )
        MonthlyCategoryValueCell(
            text = detail.variationPercent?.let { variationText(it) } ?: NO_TABLE_VALUE,
            weight = MONTHLY_WEIGHT_PERCENT,
            color = variationColor,
        )
        MonthlyCategoryValueCell(
            text = detail.changeCents?.let { signedMoney(it) } ?: NO_TABLE_VALUE,
            weight = MONTHLY_WEIGHT_MONEY,
            color = variationColor,
        )
    }
}
